import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/contexts/SupabaseAuthContext';
import { USER_ROLE, isPlatformAdmin, isCompanyAdmin, isCustomer } from '@/types/user-role';

import Layout from '@/components/Layout';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import AdminLoginPage from '@/pages/AdminLoginPage';
import NotFound from '@/pages/NotFound';
import QrScannerHelpPage from '@/pages/QrScannerHelpPage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';

// Lazy load pages
const Dashboard = React.lazy(() => import('@/pages/Dashboard'));
const WorkOrders = React.lazy(() => import('@/pages/WorkOrders'));
const WorkOrderDetail = React.lazy(() => import('@/pages/WorkOrderDetail'));
const Assets = React.lazy(() => import('@/pages/Assets'));
const AssetDetail = React.lazy(() => import('@/pages/AssetDetail'));
const Maintenance = React.lazy(() => import('@/pages/Maintenance'));
const CRM = React.lazy(() => import('@/pages/CRM'));
const Scheduling = React.lazy(() => import('@/pages/Scheduling'));
const Invoicing = React.lazy(() => import('@/pages/Invoicing'));
const Analytics = React.lazy(() => import('@/pages/Analytics'));
const Settings = React.lazy(() => import('@/pages/Settings'));
const ScanQrPage = React.lazy(() => import('@/pages/ScanQrPage'));
const CreateQrPage = React.lazy(() => import('@/pages/CreateQrPage'));
const MultiTenancyGuide = React.lazy(() => import('@/pages/MultiTenancyGuide'));
const FinancialAppGuidePage = React.lazy(() => import('@/pages/FinancialAppGuidePage'));
const NotificationsPage = React.lazy(() => import('@/pages/NotificationsPage'));
const MonthlyReportPage = React.lazy(() => import('@/pages/MonthlyReportPage'));
const PerformanceReportPage = React.lazy(() => import('@/pages/PerformanceReportPage'));
const NewWorkOrderPage = React.lazy(() => import('@/pages/NewWorkOrderPage'));
const NewAssetPage = React.lazy(() => import('@/pages/NewAssetPage'));
const NewMaintenancePage = React.lazy(() => import('@/pages/NewMaintenancePage'));
const NewCustomerPage = React.lazy(() => import('@/pages/NewCustomerPage'));
const NewInvoicePage = React.lazy(() => import('@/pages/NewInvoicePage'));
const NewSchedulingTaskPage = React.lazy(() => import('@/pages/NewSchedulingTaskPage'));
const AddSupervisorPage = React.lazy(() => import('@/pages/AddSupervisorPage'));
const AddTechnicianPage = React.lazy(() => import('@/pages/AddTechnicianPage'));
const UserManagementPage = React.lazy(() => import('@/pages/UserManagementPage'));
const StationaryEngineerPage = React.lazy(() => import('@/pages/StationaryEngineerPage'));
const FinancialsDashboard = React.lazy(() => import('@/pages/FinancialsDashboard'));
const ChartOfAccountsPage = React.lazy(() => import('@/pages/ChartOfAccountsPage'));
const TransactionsPage = React.lazy(() => import('@/pages/TransactionsPage'));
const PhotoUploadPage = React.lazy(() => import('@/pages/PhotoUploadPage'));

// Previously built but unrouted pages (wired in by deployment fix)
const SubscriptionPage = React.lazy(() => import('@/pages/SubscriptionPage'));
const SubscriptionManagementPage = React.lazy(() => import('@/pages/SubscriptionManagementPage'));
const AdminSubscriptionManagementPage = React.lazy(() => import('@/pages/admin/SubscriptionManagementPage'));
const OwnerSetupPage = React.lazy(() => import('@/pages/OwnerSetupPage'));
const TenantSetupPage = React.lazy(() => import('@/pages/TenantSetupPage'));
const GPS = React.lazy(() => import('@/pages/GPS'));

// Admin Pages
const AdminLayout = React.lazy(() => import('@/pages/admin/AdminLayout'));
const AdminDashboard = React.lazy(() => import('@/pages/admin/AdminDashboard'));
const TenantsPage = React.lazy(() => import('@/pages/admin/TenantsPage'));
const UsersPage = React.lazy(() => import('@/pages/admin/UsersPage'));
const RolesPage = React.lazy(() => import('@/pages/admin/RolesPage'));
const AdminSettingsPage = React.lazy(() => import('@/pages/admin/SettingsPage'));
const EnhancedSettingsPage = React.lazy(() => import('@/pages/admin/EnhancedSettingsPage'));
const AuditLogPage = React.lazy(() => import('@/pages/admin/AuditLogPage'));
const PropertyApprovalPage = React.lazy(() => import('@/pages/admin/PropertyApprovalPage'));
const CustomerTenantsPage = React.lazy(() => import('@/pages/admin/CustomerTenantsPage'));
const AdminAnalyticsPage = React.lazy(() => import('@/pages/admin/AnalyticsPage'));
const AdminChatPage = React.lazy(() => import('@/pages/admin/AdminChatPage'));
const ChatPage = React.lazy(() => import('@/pages/ChatPage'));
const CompanyDashboard = React.lazy(() => import('@/pages/CompanyDashboard'));
const InviteTeamPage = React.lazy(() => import('@/pages/InviteTeamPage'));
const AcceptInvitePage = React.lazy(() => import('@/pages/AcceptInvitePage'));
const CustomerPortalPage = React.lazy(() => import('@/pages/portal/CustomerPortalPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const LoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-dashed rounded-full animate-spin border-purple-400 mx-auto"></div>
      <p className="text-white text-xl mt-4">Loading TradeTitans...</p>
      <p className="text-purple-300 text-sm mt-2">Connecting to Supabase at tradetitans.repair:8000</p>
    </div>
  </div>
);

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, profile, loading } = useAuth();

  if (loading) return <LoadingFallback />;
  if (!user) return <Navigate to="/login" replace />;
  if (isCustomer(profile?.role)) return <Navigate to="/portal" replace />;

  return <>{children}</>;
};

/** Customer self-service portal */
const CustomerPortalRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, profile, loading } = useAuth();
  if (loading) return <LoadingFallback />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isCustomer(profile?.role) && !isPlatformAdmin(profile?.role)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

/** Platform console: create companies — system_admin / admin only */
const PlatformAdminRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, profile, loading } = useAuth();

  if (loading) {
    return <LoadingFallback />;
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  const userRole = profile?.role;
  if (!userRole) {
    return <Navigate to="/admin/login" replace />;
  }

  if (!isPlatformAdmin(userRole)) {
    // Company owners land on their company dashboard, not platform console
    if (isCompanyAdmin(userRole)) {
      return <Navigate to="/company" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

/** Company office: owners/supervisors manage their own customers */
const CompanyAdminRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, profile, loading } = useAuth();
  if (loading) return <LoadingFallback />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isCompanyAdmin(profile?.role) && !isPlatformAdmin(profile?.role)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

const AppRoutes: React.FC = () => (
  <Routes>
    <Route path="/login" element={<LoginPage />} />
    <Route path="/signup" element={<SignupPage />} />
    <Route path="/setup/owner" element={<OwnerSetupPage />} />
    <Route path="/admin/login" element={<AdminLoginPage />} />
    <Route path="/invite/:token" element={<AcceptInvitePage />} />
    <Route path="/portal" element={<CustomerPortalRoute><Layout><CustomerPortalPage /></Layout></CustomerPortalRoute>} />

    <Route path="/" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
    <Route path="/work-orders" element={<ProtectedRoute><Layout><WorkOrders /></Layout></ProtectedRoute>} />
    <Route path="/work-orders/new" element={<ProtectedRoute><Layout><NewWorkOrderPage /></Layout></ProtectedRoute>} />
    <Route path="/work-orders/:id" element={<ProtectedRoute><Layout><WorkOrderDetail /></Layout></ProtectedRoute>} />
    <Route path="/work-orders/:jobId/photos" element={<ProtectedRoute><Layout><PhotoUploadPage /></Layout></ProtectedRoute>} />
    <Route path="/assets" element={<ProtectedRoute><Layout><Assets /></Layout></ProtectedRoute>} />
    <Route path="/assets/new" element={<ProtectedRoute><Layout><NewAssetPage /></Layout></ProtectedRoute>} />
    <Route path="/assets/:id" element={<ProtectedRoute><Layout><AssetDetail /></Layout></ProtectedRoute>} />
    <Route path="/assets/scan" element={<ProtectedRoute><Layout><ScanQrPage /></Layout></ProtectedRoute>} />
    <Route path="/assets/create-qr" element={<ProtectedRoute><Layout><CreateQrPage /></Layout></ProtectedRoute>} />
    <Route path="/maintenance" element={<ProtectedRoute><Layout><Maintenance /></Layout></ProtectedRoute>} />
    <Route path="/maintenance/new" element={<ProtectedRoute><Layout><NewMaintenancePage /></Layout></ProtectedRoute>} />
    <Route path="/property-tasks" element={<ProtectedRoute><Layout><StationaryEngineerPage /></Layout></ProtectedRoute>} />
    <Route path="/crm" element={<ProtectedRoute><Layout><CRM /></Layout></ProtectedRoute>} />
    <Route path="/crm/new" element={<ProtectedRoute><Layout><NewCustomerPage /></Layout></ProtectedRoute>} />
    <Route path="/scheduling" element={<ProtectedRoute><Layout><Scheduling /></Layout></ProtectedRoute>} />
    <Route path="/scheduling/new-task" element={<ProtectedRoute><Layout><NewSchedulingTaskPage /></Layout></ProtectedRoute>} />
    
    {/* Subscription & tenant setup routes */}
    <Route path="/setup/tenant" element={<ProtectedRoute><TenantSetupPage /></ProtectedRoute>} />
    <Route path="/subscription" element={<ProtectedRoute><Layout><SubscriptionPage /></Layout></ProtectedRoute>} />
    <Route path="/subscription/manage" element={<ProtectedRoute><Layout><SubscriptionManagementPage /></Layout></ProtectedRoute>} />
    <Route path="/gps" element={<ProtectedRoute><Layout><GPS /></Layout></ProtectedRoute>} />

    {/* Financial Routes */}
    <Route path="/financials" element={<ProtectedRoute><Layout><FinancialsDashboard /></Layout></ProtectedRoute>} />
    <Route path="/financials/transactions" element={<ProtectedRoute><Layout><TransactionsPage /></Layout></ProtectedRoute>} />
    <Route path="/financials/chart-of-accounts" element={<ProtectedRoute><Layout><ChartOfAccountsPage /></Layout></ProtectedRoute>} />
    <Route path="/financials/invoicing" element={<ProtectedRoute><Layout><Invoicing /></Layout></ProtectedRoute>} />
    <Route path="/financials/invoicing/new" element={<ProtectedRoute><Layout><NewInvoicePage /></Layout></ProtectedRoute>} />
    <Route path="/financials/purchase-orders" element={<ProtectedRoute><Layout><PurchaseOrdersPage /></Layout></ProtectedRoute>} />

    <Route path="/analytics" element={<ProtectedRoute><Layout><Analytics /></Layout></ProtectedRoute>} />
    <Route path="/analytics/monthly-report" element={<ProtectedRoute><Layout><MonthlyReportPage /></Layout></ProtectedRoute>} />
    <Route path="/analytics/performance-report" element={<ProtectedRoute><Layout><PerformanceReportPage /></Layout></ProtectedRoute>} />
    <Route path="/settings" element={<ProtectedRoute><Layout><Settings /></Layout></ProtectedRoute>} />
    <Route path="/multi-tenancy-guide" element={<ProtectedRoute><Layout><MultiTenancyGuide /></Layout></ProtectedRoute>} />
    <Route path="/financial-app-guide" element={<ProtectedRoute><Layout><FinancialAppGuidePage /></Layout></ProtectedRoute>} />
    <Route path="/notifications" element={<ProtectedRoute><Layout><NotificationsPage /></Layout></ProtectedRoute>} />
    <Route path="/user-management" element={<ProtectedRoute><Layout><UserManagementPage /></Layout></ProtectedRoute>} />
    <Route path="/add-supervisor" element={<ProtectedRoute><Layout><AddSupervisorPage /></Layout></ProtectedRoute>} />
    <Route path="/add-technician" element={<ProtectedRoute><Layout><AddTechnicianPage /></Layout></ProtectedRoute>} />
    <Route path="/qr-scanner-help" element={<ProtectedRoute><Layout><QrScannerHelpPage /></Layout></ProtectedRoute>} />
    <Route path="/chat" element={<ProtectedRoute><Layout><ChatPage /></Layout></ProtectedRoute>} />
    <Route path="/company" element={<CompanyAdminRoute><Layout><CompanyDashboard /></Layout></CompanyAdminRoute>} />
    <Route path="/company/chat" element={<CompanyAdminRoute><Layout><ChatPage /></Layout></CompanyAdminRoute>} />
    <Route path="/company/invite" element={<CompanyAdminRoute><Layout><InviteTeamPage /></Layout></CompanyAdminRoute>} />

    <Route path="/admin" element={<PlatformAdminRoute><AdminLayout /></PlatformAdminRoute>}>
      <Route index element={<Navigate to="dashboard" replace />} />
      <Route path="dashboard" element={<AdminDashboard />} />
      <Route path="chat" element={<AdminChatPage />} />
      <Route path="tenants" element={<TenantsPage />} />
      <Route path="users" element={<UsersPage />} />
      <Route path="roles" element={<RolesPage />} />
      <Route path="settings" element={<AdminSettingsPage />} />
      <Route path="enhanced-settings" element={<EnhancedSettingsPage />} />
      <Route path="audit-logs" element={<AuditLogPage />} />
      <Route path="property-approvals" element={<PropertyApprovalPage />} />
      <Route path="subscriptions" element={<AdminSubscriptionManagementPage />} />
      <Route path="customer-sharing" element={<CustomerTenantsPage />} />
      <Route path="analytics" element={<AdminAnalyticsPage />} />
    </Route>

    <Route path="*" element={<NotFound />} />
  </Routes>
);

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <Router>
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
              <Suspense fallback={<LoadingFallback />}>
                <AppRoutes />
              </Suspense>
            </div>
          </Router>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
